/**
 * Client-side representation of a server-authoritative room.
 * Provides player tracking, events, and message sending.
 */

import {ServerWebSocket} from './ServerWebSocket'
import type {
  ServerToClientMessage,
  ServerRoomBroadcastMessage,
  ServerRoomSendToMessage,
  ServerRoomErrorMessage,
  ServerRoomPlayerJoinedMessage,
  ServerRoomPlayerLeftMessage,
  ServerPongMessage,
  ServerRoomReconnectedMessage,
  ServerRoomMovedMessage,
  ServerRoomJoinedMessage,
} from './ServerProtocol'
import type { Protocol } from '../shared'
import type { ConnectionState, RoomEvents, ServerPlayer, ServerRoom } from './ServerRoom'

type RoomPhase = 'active' | 'reconnecting' | 'dead'

export class RundotServerRoom<P extends Protocol> implements ServerRoom<P> {
  /** The shareable room code (e.g. "HX9KWR"). Updated if the server moves us. */
  roomCode: string
  readonly playerId: string
  private readonly ws: ServerWebSocket
  private _locked = false
  private readonly playersById = new Map<string, ServerPlayer>()
  private readonly _maxPlayers: number
  private readonly _isCreator: boolean
  private _phase: RoomPhase = 'active'
  private events: RoomEvents<P> = {}
  private readonly rawHandlers = new Map<string, (data: unknown) => void>()
  // Raw payloads seen for a msgType before its onRaw handler registered. This
  // buffer is LOAD-BEARING even though the syncplay transport registers its
  // handler eagerly: the ws layer parses coalesced frames synchronously in one
  // macrotask, so `session-start` can be delivered before the join promise's
  // continuation (which registers the handler) gets a microtask. Flushed and
  // cleared on the first onRaw for the msgType. With eager registration the
  // buffer only ever spans that same-macrotask window, so the caps below are
  // generous; long attach gaps are the transport's problem
  // (SyncplayRoomTransport buffers those with a loud overflow).
  private readonly pendingRaw = new Map<string, unknown[]>()
  // Bound retained payloads per msgType AND msgType cardinality so a
  // typed-only room (which never calls onRaw to drain) can't grow either
  // dimension without limit.
  private static readonly MAX_PENDING_RAW = 256
  private static readonly MAX_PENDING_RAW_TYPES = 32
  private pingInterval: ReturnType<typeof setInterval> | null = null
  private lastPingTs = 0
  private _latency = 0
  private _serverTimeOffset = 0

  /** Set to true to enable debug logging */
  static debug = false

  private log(msg: string): void {
    if (RundotServerRoom.debug) console.warn(`[RUN:transport] ${msg}`)
  }

  private warn(msg: string): void {
    if (RundotServerRoom.debug) console.warn(`[RUN:transport] ${msg}`)
  }

  private toMessage(msgType: string, data: unknown): P {
    return { ...(data as object), type: msgType } as P
  }

  /**
   * Attempt a phase transition. Returns true if the transition occurred.
   * Once `dead`, no further transitions are allowed.
   */
  private transitionTo(newPhase: RoomPhase): boolean {
    if (this._phase === 'dead' || this._phase === newPhase) return false
    this.log(`phase ${this._phase} → ${newPhase}`)
    this._phase = newPhase
    return true
  }

  constructor(joined: ServerRoomJoinedMessage, ws: ServerWebSocket) {
    this.roomCode = joined.roomCode
    this.playerId = joined.playerId
    this.ws = ws
    this._locked = joined.locked ?? false
    this._maxPlayers = joined.maxPlayers ?? 1
    this._isCreator = joined.isCreator ?? false
    for (const player of joined.players ?? [{ id: joined.playerId, username: '' }]) {
      this.playersById.set(player.id, { ...player })
    }
    this.log(`created roomCode=${joined.roomCode} playerId=${joined.playerId}`)

    // Wire up message handling
    ws.on({
      onMessage: (msg) => this.onMessageReceived(msg),
      onStateChange: (state) => this.onConnectionStateChanged(state),
      onError: (err) => this.onError(err),
    })

    // WebSocket is already connected when room is created (constructed
    // inside the room:joined handler), so start pinging immediately.
    this.startPing()
  }

  // ── Accessors ──

  /** Whether the room is locked */
  get locked(): boolean {
    return this._locked
  }

  get players(): readonly ServerPlayer[] {
    return [...this.playersById.values()]
  }

  get maxPlayers(): number {
    return this._maxPlayers
  }

  get isCreator(): boolean {
    return this._isCreator
  }

  /** Current latency in ms (round-trip / 2) */
  get latency(): number {
    return this._latency
  }

  /** Connection state */
  get connectionState(): ConnectionState {
    return this.ws.state
  }

  // ── Event Registration ──

  /**
   * Register event handlers.
   */
  on(events: RoomEvents<P>): void {
    this.events = { ...this.events, ...events }
  }

  // ── Actions ──

  /**
   * Send a typed message to the server room.
   */
  send(message: P): void {
    if (this._phase === 'dead' || this._phase === 'reconnecting') return
    const { type, ...data } = message
    this.ws.send({ type: 'message', msgType: type, data })
  }

  setSeatingOpen(open: boolean): void {
    if (this._phase === 'dead' || this._phase === 'reconnecting') return
    this.ws.send({ type: 'room:setSeatingOpen', open })
  }

  /**
   * Send an opaque payload under `msgType`, bypassing the typed-message
   * `{ type, ...data }` transform so a raw string survives the round trip.
   */
  sendRaw(msgType: string, data: unknown): void {
    if (this._phase === 'dead' || this._phase === 'reconnecting') return
    this.ws.send({ type: 'message', msgType, data })
  }

  /**
   * Register a raw handler for `msgType`. When a broadcast/sendTo arrives with a
   * matching `msgType`, its payload is delivered here untouched and the typed
   * `onMessage`/`onPrivateMessage` path is skipped for that message.
   */
  onRaw(msgType: string, handler: (data: unknown) => void): void {
    this.rawHandlers.set(msgType, handler)
    const buffered = this.pendingRaw.get(msgType)
    if (buffered) {
      this.pendingRaw.delete(msgType)
      for (const data of buffered) handler(data)
    }
  }

  /**
   * Deliver a raw payload for `msgType`. If a handler is registered, invoke it and
   * report handled=true so the typed `toMessage` path is skipped. Otherwise retain
   * the payload (bounded) so a later {@link onRaw} still receives it, and report
   * handled=false so the typed path also runs — a msgType no raw consumer ever claims
   * is delivered normally, and the retained copy is simply never drained.
   */
  private deliverRaw(msgType: string, data: unknown): boolean {
    const handler = this.rawHandlers.get(msgType)
    if (handler) {
      handler(data)
      return true
    }
    let buffered = this.pendingRaw.get(msgType)
    if (buffered === undefined) {
      if (this.pendingRaw.size >= RundotServerRoom.MAX_PENDING_RAW_TYPES) {
        return false
      }
      buffered = []
      this.pendingRaw.set(msgType, buffered)
    }
    if (buffered.length < RundotServerRoom.MAX_PENDING_RAW) {
      buffered.push(data)
    }
    return false
  }

  /** Send a room chat message (Req 20). Delivered back to all members via onChat. */
  sendChat(text: string): void {
    if (this._phase === 'dead' || this._phase === 'reconnecting') return
    this.ws.send({ type: 'message', msgType: 'chat:send', data: { text } })
  }

  /** Request recent chat history; reply arrives on onChatHistory. */
  fetchChatHistory(limit?: number): void {
    if (this._phase === 'dead' || this._phase === 'reconnecting') return
    this.ws.send({ type: 'message', msgType: 'chat:history', data: { limit } })
  }

  /**
   * Leave the room and close the connection.
   */
  leave(): void {
    if (this._phase === 'dead') return
    this._phase = 'dead'
    this.stopPing()
    this.ws.close()
  }

  /**
   * Get estimated server time (local time + offset).
   */
  getServerTime(): number {
    return Date.now() + this._serverTimeOffset
  }

  // ── Message Handling ──

  private onMessageReceived(msg: ServerToClientMessage): void {
    if (msg.type !== 'pong') {
      this.log(`handleMessage type=${msg.type}`)
    }
    switch (msg.type) {
      case 'room:broadcast':
        this.onBroadcastMessageReceived(msg)
        break
      case 'room:sendTo':
        this.onSendToMessageReceived(msg)
        break
      case 'room:lock':
        this.onLockMessageReceived()
        break
      case 'room:unlock':
        this.onUnlockMessageReceived()
        break
      case 'room:playerJoined':
        this.onPlayerJoinedMessageReceived(msg)
        break
      case 'room:playerLeft':
        this.onPlayerLeftMessageReceived(msg)
        break
      case 'room:error':
        this.onErrorMessageReceived(msg)
        break
      case 'room:reconnecting':
        this.onReconnectingMessageReceived()
        break
      case 'room:moved':
        this.onMovedMessageReceived(msg)
        break
      case 'room:reconnected':
        this.onReconnectedMessageReceived(msg)
        break
      case 'pong':
        this.onPongMessageReceived(msg)
        break
      case 'chat:message':
        this.events.onChat?.(msg.data)
        break
      case 'chat:history':
        this.events.onChatHistory?.(msg.data.messages)
        break
    }
  }

  private onBroadcastMessageReceived(msg: ServerRoomBroadcastMessage): void {
    this.log(`broadcast msgType=${msg.msgType}`)
    // Raw pass-through takes precedence: deliver the payload verbatim so an
    // opaque string envelope (e.g. deterministic 'rdm') isn't spread by toMessage.
    if (this.deliverRaw(msg.msgType, msg.data)) {
      return
    }
    // First-class world delta / resync convenience events (Req 7); everything
    // else flows through the generic typed channel.
    if (msg.msgType === 'world:delta') {
      const data = msg.data as { delta?: unknown }
      this.events.onDelta?.(data?.delta ?? msg.data)
      return
    }
    if (msg.msgType === 'world:resync') {
      const data = msg.data as { reason?: string }
      this.events.onResync?.(data?.reason ?? 'resync')
      return
    }
    this.events.onMessage?.(this.toMessage(msg.msgType, msg.data))
  }

  private onSendToMessageReceived(msg: ServerRoomSendToMessage): void {
    this.log(`sendTo msgType=${msg.msgType}`)
    if (this.deliverRaw(msg.msgType, msg.data)) {
      return
    }
    this.events.onPrivateMessage?.(this.toMessage(msg.msgType, msg.data))
  }

  private onLockMessageReceived(): void {
    this._locked = true
    this.log(`room locked`)
    this.events.onLock?.()
  }

  private onUnlockMessageReceived(): void {
    this._locked = false
    this.log(`room unlocked`)
    this.events.onUnlock?.()
  }

  private onPlayerJoinedMessageReceived(msg: ServerRoomPlayerJoinedMessage): void {
    this.log(`playerJoined id=${msg.playerId}`)
    const player = {
      id: msg.playerId,
      username: msg.username,
      avatarUrl: msg.avatarUrl,
    }
    this.playersById.set(msg.playerId, player)
    this.events.onPlayerJoined?.(player)
  }

  private onPlayerLeftMessageReceived(msg: ServerRoomPlayerLeftMessage): void {
    this.log(`playerLeft id=${msg.playerId}`)
    this.playersById.delete(msg.playerId)
    this.events.onPlayerLeft?.(msg.playerId)
  }

  private onErrorMessageReceived(msg: ServerRoomErrorMessage): void {
    // A DUPLICATE_SESSION rejection mid-reconnect means the server still holds
    // our previous seat (its disconnect grace window) or has not seen the drop
    // yet — transient by construction, and the close that accompanies it drives
    // the transport's next retry. Forwarding it as a terminal room error would
    // tear down a session that is about to recover.
    if (msg.code === 'DUPLICATE_SESSION' && this._phase === 'reconnecting') {
      this.warn(`transient reconnect rejection: ${msg.message}`)
      return
    }
    this.warn(`error: ${msg.message}`)
    this.events.onError?.(msg.message)
  }

  private onReconnectingMessageReceived(): void {
    if (this.transitionTo('reconnecting')) {
      this.events.onReconnecting?.()
    }
  }

  private onMovedMessageReceived(msg: ServerRoomMovedMessage): void {
    this.log(`moved to room=${msg._roomId} code=${msg.roomCode}`)
    // Same socket, new room: update the shareable code and the reconnect target
    // so a later reconnect resumes the destination, then let the game refetch.
    this.roomCode = msg.roomCode
    this.ws.retargetRoom(msg._roomId, msg.roomCode)
    this.events.onMoved?.(msg._roomId)
  }

  private onReconnectedMessageReceived(_msg: ServerRoomReconnectedMessage): void {
    this.ws.confirmConnection()
    if (this.transitionTo('active')) {
      this.events.onReconnected?.()
    }
  }

  private onPongMessageReceived(msg: ServerPongMessage): void {
    const now = Date.now()
    const rtt = now - msg.ts
    this._latency = Math.round(rtt / 2)
    this._serverTimeOffset = msg.serverTs - now + this._latency
  }

  private onError(err: Error): void {
    this.warn(`ws error: ${err.message}`)
    this.events.onError?.(err.message)
  }

  private onConnectionStateChanged(state: ConnectionState): void {
    this.log(`connectionState=${state}`)
    switch (state) {
      case 'connected':
        this.onConnectedStateEntered()
        break
      case 'disconnected':
        this.onDisconnectedStateEntered()
        break
      case 'reconnecting':
        this.onReconnectingStateEntered()
        break
    }
  }

  private onConnectedStateEntered(): void {
    this.startPing()
  }

  private onDisconnectedStateEntered(): void {
    this.stopPing()
    if (this.transitionTo('dead')) {
      this.events.onDisconnect?.()
    }
  }

  private onReconnectingStateEntered(): void {
    this.stopPing()
    if (this.transitionTo('reconnecting')) {
      this.events.onReconnecting?.()
    }
  }

  private startPing(): void {
    if (this.pingInterval) return
    this.pingInterval = setInterval(() => {
      this.lastPingTs = Date.now()
      this.ws.send({ type: 'ping', ts: this.lastPingTs })
    }, 5000)
  }

  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }
  }
}
